import React, { useEffect, useRef, useState, useMemo } from "react"
import Flickity from "flickity"

import { useSearchspringRecommendResults } from "@app/scripts/hooks/use-searchspring-recommend-results"
import { CompactProductCard } from "@app/scripts/components/compact-product-card"
import { chevronRight, chevronLeft } from "../../hooks/icons"

import { currentCountryCurrency } from "@app/scripts/lib/utils"
import {
  useShopifyPrice,
  mergeResultsWithPricing,
} from "@app/scripts/hooks/use-shopify"
type CartRecommendation = {
  title: string
  product_id: string
  profile_id: string
}

type recommendations = {
  heading: string
  // Comma-separated cart SKUs, rendered fresh with each cart drawer update
  cart?: string
}

export type Props = {
  cart_recommendations: CartRecommendation
  recommendationsData: recommendations
}

export function CartRecommendations({ recommendationsData }: Props) {
  const [canPrev, setCanPrev] = useState(false)
  const [canNext, setCanNext] = useState(false)

  const results = useSearchspringRecommendResults()

  const carouselRef = useRef<HTMLDivElement>(null)
  const flickityInstance = useRef<Flickity | null>(null)

  // Formats data for useShopifyPrice
  const formattedProductData = useMemo(() => {
    return results.map((result) => ({
      price: result?.price || 0,
      compareAtPrice: result?.msrp || 0,
      id: result?.attributes?.ss_id || "",
      handle: result?.attributes?.handle || "",
    }))
  }, [results])

  // Formats data (returns new prices or AUD prices)
  const formattedPriceData = useShopifyPrice(
    formattedProductData,
    currentCountryCurrency
  )

  // Create new products array with updated price data or defaults back to results
  const productsWithUpdatedPrices = useMemo(() => {
    return mergeResultsWithPricing(results, formattedPriceData)
  }, [results, formattedPriceData])

  useEffect(() => {
    if (!carouselRef.current) return

    flickityInstance.current = new Flickity(carouselRef.current, {
      cellAlign: "left",
      pageDots: false,
      contain: true,
      imagesLoaded: true,
      prevNextButtons: false,
      groupCells: 1,
    })

    const flkty = flickityInstance.current

    const updateNavButtons = () => {
      setCanPrev(flkty.selectedIndex > 0)
      setCanNext(flkty.selectedIndex < flkty.slides.length - 1)
    }

    updateNavButtons()
    flkty.on("select", updateNavButtons)

    return () => {
      flickityInstance.current?.destroy()
      flickityInstance.current = null
    }
  }, [productsWithUpdatedPrices])

  const goPrev = () => flickityInstance.current?.previous()
  const goNext = () => flickityInstance.current?.next()

  if (!productsWithUpdatedPrices.length) return null

  return (
    <div className="cart-recommendations--container">
      <div className="cart-recommendations--header">
        {recommendationsData?.heading && (
          <p className="cart-recommendations--heading">
            {recommendationsData?.heading}
          </p>
        )}
        <div className="product-carousel--buttons-container">
          <button
            className="product-carousel--prev-button"
            onClick={goPrev}
            disabled={!canPrev}
          >
            {chevronLeft}
          </button>
          <button
            className="product-carousel--next-button"
            onClick={goNext}
            disabled={!canNext}
          >
            {chevronRight}
          </button>
        </div>
      </div>

      <div
        className="cart-recommendations--product-container carousel"
        ref={carouselRef}
      >
        {productsWithUpdatedPrices?.length > 0 &&
          productsWithUpdatedPrices?.map((product, i) => (
            <div className="carousel-cell" key={i}>
              <CompactProductCard
                product={product}
                currentCountryCurrency={currentCountryCurrency}
              />
            </div>
          ))}
      </div>
    </div>
  )
}
